/**
 * **The password keeper** (W22, FR-58, plan D56): a PDF's password, held in
 * this tab's memory after it unlocked a reading, so the preview and the review's
 * evidence page can draw the pages **in the browser** without asking again.
 *
 * What it is, and what it never is:
 *
 * - **A module-level `Map<versionId, string>`** — the bytes of one version are
 *   protected by one password, and a later version may have another.
 * - **Never browser storage.** Nothing here touches `localStorage`,
 *   `sessionStorage`, IndexedDB, a cookie or the address; a reload or a closed
 *   tab forgets every password, and the pages ask again
 *   (`tests/password.test.tsx` spies on all of them).
 * - **Never sent anywhere but the one parse that used it** — the stored file is
 *   never decrypted or replaced, and its download stays the protected original.
 * - **Cleared by the host** on an organisation switch and on sign-out
 *   (`passwordKeeper.clear()`), so a password never follows somebody into
 *   another organisation's screens.
 *
 * Only a password a completed reading proved is kept: a wrong one is dropped
 * at once, and so is one the pages then fail to open with.
 */
import { useSyncExternalStore } from 'react'

const kept = new Map<string, string>()
const listeners = new Set<() => void>()
let revision = 0

function emit() {
  revision += 1
  for (const listener of listeners) listener()
}

export interface PasswordKeeper {
  /** Hold `password` for this version, in this tab's memory only. */
  keep: (versionId: string, password: string) => void
  /** The password held for this version, or `null` — it stays held. */
  take: (versionId: string | null | undefined) => string | null
  /** Drop this version's password — it did not open the pages. */
  forget: (versionId: string) => void
  /** Drop every password — the host's org switch and sign-out. */
  clear: () => void
}

export const passwordKeeper: PasswordKeeper = Object.freeze({
  keep: (versionId: string, password: string) => {
    if (!versionId || !password) return
    kept.set(versionId, password)
    emit()
  },
  take: (versionId: string | null | undefined) => (versionId ? (kept.get(versionId) ?? null) : null),
  forget: (versionId: string) => {
    if (kept.delete(versionId)) emit()
  },
  clear: () => {
    if (kept.size === 0) return
    kept.clear()
    emit()
  },
})

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** The password kept for a version, re-rendered when one is kept or dropped — so the pages open as soon as an unlock completes elsewhere on the screen. */
export function useKeptPassword(versionId: string | null | undefined): string | null {
  useSyncExternalStore(subscribe, () => revision)
  return passwordKeeper.take(versionId)
}
